// @ts-nocheck
import {DataModel, EntityModel, FolderModel, FolderEntitiesModel} from '/ui/components/entitypicker/datasource/datamodel';
import {CollapsibleItem} from '/ui/components/entitypicker/entitylist/collapsibleitem';
import {EntityItem} from '/ui/components/entitypicker/entitylist/entityitem';
import {SeeMoreItem} from '/ui/components/entitypicker/entitylist/seemoreitem';
import {yext} from '/ui/lib/msg';

import * as styles from '/ui/components/entitypicker/entitylist/itemlist.module.scss';
import FolderIcon from '/ui/icons/inline/folder.svg';


/**
 * @typedef {{
 *   folderEntitiesModel: (!FolderEntitiesModel|undefined),
 *   folders: !Array<!FolderModel>,
 * }}
 */
let FolderInfo;

/**
 * @typedef {Map<number, !FolderInfo>}
 */
let ChildMap;

/**
 * Creates a mapping from folder ID to entity & folder children.
 * @param {!DataModel} data
 * @return {ChildMap}
 */
const buildChildrenMap = data => {
  /** @type {!ChildMap} */
  const map = new Map();

  for (const folder of /** @type {!Array<!FolderModel>} */(data.folders.items)) {
    map.set(folder.id, {
      folderEntitiesModel: data.folderEntities.get(folder.id),
      folders: [],
    });
  }

  for (const folder of /** @type {!Array<!FolderModel>} */(data.folders.items)) {
    if (folder.parentId) {
      const parent = map.get(folder.parentId);
      parent && parent.folders.push(folder);
    }
  }

  return map;
};

/**
 * @param {{
 *   data: !DataModel,
 *   childMap: !ChildMap,
 *   folder: !FolderModel,
 *   onSelect: function(!EntityModel): void,
 *   onShowMore: function(string): void,
 * }} props
 * @return {React.ReactElement}
 */
const EntityTreeFolder = ({
  data,
  childMap,
  folder,
  onSelect,
  onShowMore,
}) => {
  if (!childMap.has(folder.id)) {
    return (
      <CollapsibleItem
        icon={<FolderIcon />}
        label={folder.label} />
    );
  }

  const mapped = /** @type {!FolderInfo} */(childMap.get(folder.id));
  const childFolders = mapped.folders;
  const totalDescendants = mapped.folderEntitiesModel && mapped.folderEntitiesModel.total || 0;
  const childEntities = mapped.folderEntitiesModel && mapped.folderEntitiesModel.entities;
  const nextPageQuery = mapped.folderEntitiesModel && mapped.folderEntitiesModel.nextPageQuery;

  return (
    <CollapsibleItem
      icon={<FolderIcon />}
      label={`${folder.label} (${totalDescendants})`}
      role="treeitem"
      collapsible={totalDescendants + childFolders.length > 0}>
      <ul role="group">
        {
          childFolders.map(childFolder => (
            <EntityTreeFolder
              data={data}
              childMap={childMap}
              folder={childFolder}
              key={childFolder.id}
              onSelect={onSelect}
              onShowMore={onShowMore} />
          ))
        }
        {
          childEntities && childEntities.map(entity => (
            <EntityItem
              entity={entity}
              entityType={entity.typeId}
              onClick={() => onSelect(entity)}
              key={entity.id}
              role="treeitem" />
          ))
        }
        {
          nextPageQuery
            && <SeeMoreItem onClick={() => onShowMore(/** @type{string} */(nextPageQuery))} />
        }
      </ul>
    </CollapsibleItem>
  );
};

/**
 * @param {{
 *   data: !DataModel,
 *   onSelect: function(!EntityModel): void,
 *   onShowMore: function(string): void,
 * }} props
 * @returns {React.ReactElement}
 */
export const EntityTree = ({
  data,
  onSelect,
  onShowMore,
}) => {
  const childMap = React.useMemo(() => buildChildrenMap(data), [data]);
  const topLevelFolders = data.folders.items.filter(folder => !folder.parentId || !childMap.has(folder.parentId));

  if (topLevelFolders.length === 0) {
    return <div className={styles.noResultsMessage}>{yext.msg('There were no entities found')}</div>;
  }

  return (
    <ul role="tree">
      {
        topLevelFolders.map(folder => (
          <EntityTreeFolder
            data={data}
            childMap={childMap}
            folder={folder}
            key={folder.id}
            onSelect={onSelect}
            onShowMore={onShowMore} />
        ))
      }
    </ul>
  );
};
